import {
  ROUND_TITLE_BY_ROUND_TYPE,
  SPELLING_BEE_SETTINGS,
} from "app/data/game-settings";
import { RoundType, type WordData } from "app/game-data/useFetchGameData";
import useGameData from "app/game-data/useGameData";
import { useGameState } from "app/game/useGame";
import getWordCorrectness from "app/score/getWordCorrectness";
import getWordScore from "app/score/getWordScore";
import { ScoreAction } from "app/score/scoreState";
import { useScoreDispatch, useScoreState } from "app/score/useScore";
import { SpellingBeeProvider } from "app/spelling-bee/SpellingBeeProvider";
import { SpellingBeeAction } from "app/spelling-bee/spellingBeeState";
import {
  useSpellingBeeActionDispatch,
  useSpellingBeeState,
} from "app/spelling-bee/useSpellingBee";
import KeyboardGroup from "app/spelling-input/KeyboardGroup";
import Header from "app/ui/Header";
import NextRoundButton from "app/ui/NextRoundButton";
import RoundIntroScreen from "app/ui/RoundIntroScreen";
import WordClues, { type WordCluesHandle } from "app/ui/WordClues";
import WordSpellingRecord from "app/ui/WordSpellingRecord";
import cn from "app/utils/classnames";
import usePreloadedAudio from "app/utils/usePreloadedAudio";
import mixpanel from "mixpanel-browser";
import { useRef } from "react";

interface SpellingBeeRoundProps {
  words: Array<WordData>;
  roundIndex: number;
  onComplete: () => void;
  className?: string;
}

function SpellingBeeRound({
  words,
  roundIndex,
  onComplete,
  className,
}: SpellingBeeRoundProps) {
  const gameData = useGameData();
  const roundState = useSpellingBeeState();
  const roundDispatch = useSpellingBeeActionDispatch();
  const scoreDispatch = useScoreDispatch();
  const scoreState = useScoreState();
  const wordCluesRef = useRef<WordCluesHandle>(null);
  const preloadedAudio = usePreloadedAudio();
  const { inputType } = useGameState();

  const roundComplete = roundState.completedWordIndex > words.length - 1;
  const currentWord =
    words[roundState.completedWordIndex] || words[words.length - 1];

  function getNextAudioUrl(): string | undefined {
    if (roundState.isOnIntro) {
      return words[0].wordAudioUrl;
    }
    if (roundState.completedWordIndex >= words.length - 1) {
      return "";
    }
    return words[roundState.completedWordIndex + 1].wordAudioUrl;
  }

  function handleSpellingInputSubmit(word: string) {
    // Stop all audio
    preloadedAudio.stop();
    wordCluesRef.current?.forceStop();

    // Play the clip for the next word
    if (roundState.completedWordIndex < words.length - 1) {
      preloadedAudio.preload(getNextAudioUrl() ?? "");
      wordCluesRef.current?.forcePlay(preloadedAudio);
    }

    const userSpelling = word.toLocaleLowerCase();
    const correctSpelling = currentWord.wordText.toLocaleLowerCase();
    const match = getWordCorrectness(userSpelling, correctSpelling);
    const points = getWordScore(SPELLING_BEE_SETTINGS.maxWordScore, match);

    scoreDispatch({
      type: ScoreAction.WORD_SPELLED,
      payload: {
        roundIndex: roundIndex,
        userSpelling: userSpelling,
        correctSpelling: correctSpelling,
        pointValue: points,
      },
    });

    mixpanel.track("spelling_submitted", {
      game_id: gameData?.id,
      level_type: RoundType.SPELLING_BEE,
      user_spelling: userSpelling,
      correct_spelling: correctSpelling,
    });

    scoreDispatch({
      type: ScoreAction.POINTS_EARNED,
      payload: { roundIndex: roundIndex, points: points },
    });

    if (userSpelling === correctSpelling) {
      roundDispatch({ type: SpellingBeeAction.ANSWERED_CORRECTLY });
    } else {
      roundDispatch({ type: SpellingBeeAction.ANSWERED_INCORRECTLY });
    }
  }

  function handleNextRoundClick() {
    roundDispatch({ type: SpellingBeeAction.ROUND_COMPLETED });
    onComplete();
  }

  return (
    <div className="w-full h-full relative">
      <div
        className={cn(
          "h-full",
          "flex flex-col justify-between items-center gap-3 lg:gap-5",
          className,
        )}
      >
        <div // GAME CONTENT
          className={cn("w-full", "flex flex-col gap-4 lg:justify-center")}
        >
          <Header>{ROUND_TITLE_BY_ROUND_TYPE[RoundType.SPELLING_BEE]}</Header>
          <WordSpellingRecord
            numTotalWords={words.length}
            completedSpellings={
              scoreState.roundScores[roundIndex]?.wordSpellings
            }
            className="mb-2"
          />
          {roundComplete ? (
            <NextRoundButton
              className="m-auto mt-2"
              onClick={handleNextRoundClick}
              allowEnter={true}
            />
          ) : (
            <WordClues
              wordAudioUrl={currentWord.wordAudioUrl}
              definitionAudioUrl={currentWord.definitionAudioUrl}
              etymologyAudioUrl={currentWord.originAudioUrl}
              ref={wordCluesRef}
              className="min-w-50"
            />
          )}
        </div>
        {!roundState.isOnIntro && !roundComplete && (
          <div
            className={cn("w-full flex-none", inputType === "mouse" && "grow")}
          >
            <KeyboardGroup
              heightShrinkCutoff={650}
              onSubmit={handleSpellingInputSubmit}
            />
          </div>
        )}
      </div>
      {roundState.isOnIntro && (
        <RoundIntroScreen
          className="absolute top-0 bottom-0 -left-5 -right-5"
          roundType={RoundType.SPELLING_BEE}
          roundIndex={roundIndex}
          onContinueClick={() => {
            preloadedAudio.preload(getNextAudioUrl() ?? "");
            wordCluesRef.current?.forcePlay(preloadedAudio);
            roundDispatch({ type: SpellingBeeAction.INTRO_CLOSED });
          }}
        />
      )}
    </div>
  );
}

export default function WrappedSpellingBeeRound(props: SpellingBeeRoundProps) {
  return (
    <SpellingBeeProvider key={props.roundIndex} roundIndex={props.roundIndex}>
      <SpellingBeeRound {...props} />
    </SpellingBeeProvider>
  );
}
